<script setup>
// ========================================
// Imports
// ========================================
import { computed } from 'vue'
import { useDisplay } from 'vuetify'
import { useI18n } from 'vue-i18n'
import { useModalStore } from '@/stores/ModalStore'

// ========================================
// Props & Models
// ========================================
const props = defineProps({
  length: {
    type: Number,
    required: true,
  },
  pageSizeItems: {
    type: Array,
    default: () => [5, 10, 15, 30],
  },
  label: {
    type: String,
    default: null,
  },
  tooltip: {
    type: String,
    default: null,
  },
  showResultsSummary: {
    type: Boolean,
    default: false,
  },
  filteredCount: {
    type: Number,
    default: 0,
  },
  totalCount: {
    type: Number,
    default: 0,
  },
  what: {
    type: String,
    default: '',
  },
  totalChipped: {
    type: Boolean,
    default: false,
  },

  // First row items pushed to the left and right edges instead of centered
  spaceBetween: {
    type: Boolean,
    default: false,
  },

  // ModalStore modal name; when set, a "Cancel" button closing that modal is shown in the first row
  closeModal: {
    type: String,
    default: '',
  },

  // @cancel listener, for modals not handled by ModalStore; its presence also shows the "Cancel" button
  onCancel: {
    type: Function,
    default: null,
  },
})

const page = defineModel({
  type: Number,
  required: true,
})

const perPage = defineModel('perPage', {
  type: Number,
  required: true,
})

// ========================================
// Logic & Computed
// ========================================
const { t } = useI18n()
const { xs, mobile } = useDisplay()
const modalStore = useModalStore()

const selectLabel = computed(() => props.label ?? t('Per page'))
const tooltipText = computed(() => props.tooltip ?? t('number_of_results_per_page'))

const rangeStart = computed(() => {
  if (props.filteredCount === 0) return 0

  return (page.value - 1) * perPage.value + 1
})

const rangeEnd = computed(() => Math.min(page.value * perPage.value, props.filteredCount))
const isFiltered = computed(() => props.filteredCount !== props.totalCount)
const rangeText = computed(() => `${rangeStart.value} - ${rangeEnd.value}`)
const whatSuffix = computed(() => props.what ? ` ${props.what}` : '')

const showCancel = computed(() => !!props.closeModal || !!props.onCancel)

const cancel = () => {
  if (props.closeModal) modalStore.closeModal(props.closeModal)
  else props.onCancel()
}

// Phone + cancel button: only prev/next arrows, so cancel, arrows and per page select fit in one row
const compact = computed(() => xs.value && showCancel.value)

// Max page numbers shown. Next to the cancel button only 3 and no first/last buttons
// (max 7 pagination buttons, e.g. ‹ 1 … 3 … 9 ›), otherwise in a 560px modal
// cancel + up to 9 pagination buttons + per page select don't fit and the select wraps to a new line
const totalVisible = computed(() => {
  if (compact.value) return 0
  if (xs.value) return 1

  return showCancel.value ? 3 : 5
})
</script>

<template>
  <div>
    <!-- xs without cancel: pagination on its own line, per page select centered below -->
    <VCol
      cols="12"
      class="d-flex align-center flex-wrap"
      :class="spaceBetween || compact ? 'justify-space-between' : 'justify-center'"
    >
      <VBtn
        v-if="showCancel"
        variant="outlined"
        color="secondary"
        :class="{ 'me-4': !xs && !spaceBetween }"
        @click="cancel"
      >
        {{ $t('cancel') }}
      </VBtn>
      <!-- No results: only the cancel button stays -->
      <template v-if="props.length > 0">
        <!-- compact: no page numbers and no first/last, only prev/next arrows next to the select -->
        <VPagination
          v-model="page"
          :length="props.length"
          :total-visible="totalVisible"
          :show-first-last-page="!showCancel"
          density="comfortable"
          color="primary"
          :class="compact ? 'ms-auto me-2' : { 'w-100 mb-3': xs }"
        />
        <!-- Side margins only for the centered desktop layout; space-between spaces items itself -->
        <span :class="{ 'ml-4': !xs && !spaceBetween }">
          <VSelect
            v-model="perPage"
            style="inline-size: 110px;"
            :label="selectLabel"
            :items="props.pageSizeItems"
            density="compact"
            color="secondary"
            hide-details
            :class="{ 'ml-4': !xs && !spaceBetween }"
          />
          <VTooltip
            v-if="!mobile"
            activator="parent"
          >
            {{ tooltipText }}
          </VTooltip>
        </span>
      </template>
    </VCol>

    <!-- pt-0 on phones: summary closer to the buttons row -->
    <VCol
      v-if="showResultsSummary && filteredCount > 0"
      cols="12"
      class="d-flex justify-center align-center flex-column flex-sm-row pt-0 pt-sm-3"
    >
      <div
        v-if="filteredCount > 0"
        class="text-body-2 text-medium-emphasis text-center"
      >
        <template v-if="isFiltered">
          {{ rangeText }} z {{ filteredCount }} {{ $t('filtered') }}{{ whatSuffix }} ({{ $t('of') }}
          <VChip
            v-if="totalChipped"
            color="primary"
            size="small"
            class="mx-1"
          >
            {{ totalCount }}
          </VChip>
          <template v-else>
            {{ totalCount }}
          </template>
          {{ $t('all') }})
        </template>
        <template v-else>
          {{ rangeText }} {{ $t('of') }}
          <VChip
            v-if="totalChipped"
            color="primary"
            size="small"
            class="mx-1"
          >
            {{ totalCount }}
          </VChip>
          <template v-else>
            {{ totalCount }}
          </template>
          {{ whatSuffix }}
        </template>
      </div>
    </VCol>
  </div>
</template>


